import { inject, Injectable } from '@angular/core';
import { PopupService } from '@uigy/shared/services/popup.service';
import { Router } from '@angular/router';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { LanguageService } from '@uigy/shared/services/language.service';
import { LicenseManagerService } from '@uigy/modules/license/license-manager.service';
import { filter, take } from 'rxjs/operators';
import { LicenseStatus } from '@uigy/generated/graphql';
import { IemService } from '@uigy/shared/services/iem.service';
import { LocalStorageService } from '@uigy/shared/services/local-storage.service';
import { LocalStorageEnum } from '@uigy/shared/enums/storageKeys.enum';
import { FeatureFlagService } from '@uigy/shared/services/feature-flag.service';
import { FeatureFlags } from '@uigy/shared/enums/feature-flags.enum';
import {
  NetworkManagementTabsComponent,
} from '@uigy/modules/settings/modules/assets/components/network-management-tabs/network-management-tabs.component';

@Injectable({
  providedIn: 'root',
})
export class LoginOnboardingService {
  private popupService = inject(PopupService);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private licenseManagerService = inject(LicenseManagerService);
  private iemService = inject(IemService);
  private localStorageService = inject(LocalStorageService);
  private featureFlagService = inject(FeatureFlagService);

  constructor() {
  }

  /**
   * Handles showing the onboarding popup when the license status is Active and the popup should be shown, specifically in ICP.
   *
   * Note: This uses LocalStorage (not ExternalStorage) because the flag is set during setup wizard,
   * which runs before user authentication. ExternalStorage requires an authenticated session.
   */
  public handleOnboardingPopup(): void {
    if (this.iemService.isIEM()) {
      return;
    }

    if (!this.shouldShowOnboardingPopup()) {
      return;
    }

    this.licenseManagerService.getLicenseStatus().pipe(
      take(1),
      filter(status => status === LicenseStatus.Active),
    ).subscribe(() => {
      this.openWelcomePopup();
    });
  }

  private openWelcomePopup(): void {
    this.popupService.openPopup({
      header: this.languageService.texts.onboarding.welcome_popup.header,
      text: this.languageService.texts.onboarding.welcome_popup.title,
      submit: {
        execute: () => {
          this.reset();
          this.featureFlagService.getFlag(FeatureFlags.subnetsManagement)
            ? this.router.navigate([RoutesStatesNamesEnum.settings, RoutesStatesNamesEnum.settingsAssets, RoutesStatesNamesEnum.networksManagement],
              {
                queryParams: NetworkManagementTabsComponent.defaultNavigation
              },
            )
            : this.router.navigate([RoutesStatesNamesEnum.settings, RoutesStatesNamesEnum.settingsAssets, RoutesStatesNamesEnum.assetSettings]);
        },
        title:
          this.featureFlagService.getFlag(FeatureFlags.subnetsManagement)
            ? this.languageService.texts.onboarding.welcome_popup.submit
            : this.languageService.texts.onboarding.welcome_popup.submit_no_feature_flag,
      },
      cancel: {
        title: this.languageService.texts.general.buttons.dismiss,
        execute: () => this.reset(),
      },
    });
  }

  private shouldShowOnboardingPopup(): boolean {
    const value = this.localStorageService.getLocalStorageItemByKey(LocalStorageEnum.ShouldShowOnboardingPopup);
    return value === 'true';
  }

  private reset(): void {
    this.localStorageService.addKeyValueToLocalStorage(LocalStorageEnum.ShouldShowOnboardingPopup, 'false');
  }
}
